<template>
  <!-- Teleport: `.measure-selection` bildet per `backdrop-blur` einen eigenen Stacking-Context.
       Darin ist jeder z-index wirkungslos gegen die danach folgende Notenvorschau (sticky Toolbar
       z-10, Overlays z-20) – das Popover der letzten Kachelzeile lag sonst dahinter. -->
  <Teleport to="body">
  <div
      ref="panelRef"
      class="variant-picker"
      :style="{top: `${panelTop}px`, left: `${panelLeft}px`, maxHeight: `${panelMaxHeight}px`}"
  >
    <div class="picker-header">
      <span class="text-sm font-semibold text-slate-700">Variante wählen</span>
      <button class="picker-close" type="button" @click="close">
        <Icon icon="fa6-solid:xmark"/>
      </button>
    </div>

    <div class="picker-grid">
      <button
          v-for="variant in dedupedVariants"
          :key="variant.variant_name"
          type="button"
          class="picker-option"
          :class="{
            'picker-option--current': variant.content_hash === currentHash,
            'picker-option--unplayable': !variant.playable,
          }"
          @click="emit('select', variant.variant_name)"
      >
        <div class="picker-option-head">
          <span :class="variant.direction === 'pull' ? 'text-blue-600' : 'text-slate-900'">
            {{ variant.label }}
          </span>
          <span class="picker-score">{{ Math.round(variant.score) }}</span>
          <Icon v-if="!variant.playable" icon="fa6-solid:triangle-exclamation" class="text-red-500"/>
          <Icon v-if="variant.content_hash === currentHash" icon="fa6-solid:check" class="text-green-600"/>
        </div>
        <OsmdMeasure :music-xml="variant.music_xml" :zoom="0.25"/>
      </button>
    </div>
  </div>
  </Teleport>
</template>

<script setup lang="ts">
import {computed, ref} from 'vue';
import {onClickOutside, useElementBounding, useWindowSize} from '@vueuse/core';
import {Icon} from '@iconify/vue';
import type {MeasureVariant} from '@/generated';
import {clampedPosition, PANEL_MARGIN} from '@/components/musicxml-preview/panelPosition';
import {useCaptureEscape} from '@/composables/useCaptureEscape';
import OsmdMeasure from './OsmdMeasure.vue';

const {variants, current, anchor = null} = defineProps<{
  variants: MeasureVariant[];
  current: string;
  /** Kachel, an der das (per Teleport `fixed` schwebende) Popover hängt. */
  anchor?: HTMLElement | null;
}>();

const emit = defineEmits<{
  (e: 'select', variantName: string): void;
  (e: 'close'): void;
}>();

// ============ Position (fixed, an der Kachel verankert) ============

/** Abstand zwischen Kachel und Popover (px). */
const ANCHOR_GAP = 4;
/** Ab so viel Platz unter der Kachel bleibt das Popover unten, auch wenn oben mehr frei ist. */
const MIN_SPACE_BELOW = 320;
/** Obergrenze der Popover-Höhe (px), unabhängig vom freien Platz. */
const MAX_PANEL_HEIGHT = 800;

const panelRef = ref<HTMLElement | null>(null);
// useElementBounding folgt auch dem Scrollen in <main> (capture-Scroll-Listener).
const anchorBox = useElementBounding(() => anchor);
const {width: panelWidth, height: panelHeight} = useElementBounding(panelRef);
const {width: windowWidth, height: windowHeight} = useWindowSize();

const spaceBelow = computed(() => windowHeight.value - anchorBox.bottom.value - ANCHOR_GAP - PANEL_MARGIN);
const spaceAbove = computed(() => anchorBox.top.value - ANCHOR_GAP - PANEL_MARGIN);
// Entscheidung nur über den freien Platz (nicht über die gemessene Höhe), sonst schaukelt sich
// Platzierung ↔ max-height gegenseitig auf.
const placeBelow = computed(() => spaceBelow.value >= MIN_SPACE_BELOW || spaceBelow.value >= spaceAbove.value);

const panelMaxHeight = computed(() =>
    Math.min(MAX_PANEL_HEIGHT, Math.max(0, placeBelow.value ? spaceBelow.value : spaceAbove.value)),
);

const panelTop = computed(() =>
    placeBelow.value
        ? anchorBox.bottom.value + ANCHOR_GAP
        : Math.max(PANEL_MARGIN, anchorBox.top.value - ANCHOR_GAP - panelHeight.value),
);

const panelLeft = computed(() =>
    clampedPosition(
        anchor ? anchorBox.left.value + anchorBox.width.value / 2 : null,
        panelWidth.value,
        windowWidth.value,
        PANEL_MARGIN,
    ),
);

// ============ Schließen ============

function close(): void {
  emit('close');
}

// Die Kachel selbst ausnehmen: ihr Klick schaltet `open` bereits um – sonst schlösse
// pointerdown und der folgende click öffnete wieder.
onClickOutside(panelRef, (event) => {
  if (anchor && event.target instanceof Node && anchor.contains(event.target)) return;
  close();
});
useCaptureEscape(true, close);

// Hash of the currently selected variant, so we can highlight the (possibly
// deduped) representative that is physically identical to the current choice.
const currentHash = computed(() => variants.find((v) => v.variant_name === current)?.content_hash ?? '');

// Collapse physically identical variants (same buttons) to a single representative,
// keeping the best (lowest) score, sorted best-first.
const dedupedVariants = computed<MeasureVariant[]>((oldValue) => {
  const byHash = new Map<string, MeasureVariant>();
  for (const variant of variants) {
    const existing = byHash.get(variant.content_hash);
    if (!existing || variant.score < existing.score) {
      byHash.set(variant.content_hash, variant);
    }
  }
  const result = [...byHash.values()].sort((a, b) => a.score - b.score);

  if (
      oldValue &&
      oldValue.length === result.length &&
      oldValue.every((o, i) => o.variant_name === result[i]?.variant_name)
  ) {
    return oldValue;
  }
  return result;
});
</script>

<style scoped>
@import 'tailwindcss';

.variant-picker {
  @apply fixed z-50 flex w-[22rem] max-w-[90vw] flex-col
  rounded-xl border border-orange-400 bg-orange-100 p-3 shadow-xl;
}

.picker-header {
  @apply mb-2 flex items-center justify-between;
}

.picker-close {
  @apply rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700;
}

.picker-grid {
  @apply grid min-h-0 flex-1 grid-cols-1 gap-0 overflow-y-auto bg-gray-100/50;
}

.picker-option {
  @apply flex flex-col max-h-[300px] gap-0 rounded-lg border border-slate-200 bg-slate-50 p-2 text-left
  transition-all hover:border-blue-400 hover:bg-blue-50 scale-80;

}

.picker-option--current {
  @apply border-green-400 hover:border-blue-400 bg-green-50/50;
}

.picker-option--unplayable {
  @apply border-red-200;
}

.picker-option-head {
  @apply flex items-center gap-1 text-xs font-semibold;
}

.picker-score {
  @apply ml-auto rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-600;
}
</style>
